import { SectionId } from "@eventr/common";
import type { DraftItem } from "utils/merge-draft-items";

export type DraftSectionItems = Record<number, Partial<Record<SectionId, DraftItem[]>>>;
export type DraftStagedAt = Record<number, Partial<Record<SectionId, Record<string, number>>>>;

/** Drafts staged longer than this are dropped at rehydrate, not kept indefinitely. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Drops items whose recorded staging time is older than DRAFT_TTL_MS.
 * Items without a recorded time are kept, so a missing timestamp never drops work.
 */
export function pruneExpiredDraftSectionItems(
  drafts: DraftSectionItems | undefined,
  stagedAt: DraftStagedAt | undefined,
  now: number = Date.now(),
): { drafts: DraftSectionItems; stagedAt: DraftStagedAt } {
  const prunedDrafts: DraftSectionItems = {};
  const prunedStagedAt: DraftStagedAt = {};
  if (!drafts) return { drafts: prunedDrafts, stagedAt: prunedStagedAt };

  for (const itinKey of Object.keys(drafts)) {
    const itinID = Number(itinKey);
    const itin = drafts[itinID];
    if (!itin) continue;

    const itinStagedAt = stagedAt?.[itinID];
    const prunedItin: Partial<Record<SectionId, DraftItem[]>> = {};
    const prunedItinStagedAt: Partial<Record<SectionId, Record<string, number>>> = {};

    for (const sectionKey of Object.keys(itin)) {
      const sectionId = Number(sectionKey) as SectionId;
      const items = itin[sectionId];
      if (!items?.length) continue;

      const sectionStagedAt = itinStagedAt?.[sectionId];
      const kept: DraftItem[] = [];
      const keptStagedAt: Record<string, number> = {};
      for (const item of items) {
        const staged = sectionStagedAt?.[item._id];
        if (typeof staged !== "number" || now - staged < DRAFT_TTL_MS) {
          kept.push(item);
          // Only surviving items with a recorded time carry one into the result.
          if (typeof staged === "number") keptStagedAt[item._id] = staged;
        }
      }
      if (!kept.length) continue;

      prunedItin[sectionId] = kept;
      prunedItinStagedAt[sectionId] = keptStagedAt;
    }

    if (!Object.keys(prunedItin).length) continue;
    prunedDrafts[itinID] = prunedItin;
    prunedStagedAt[itinID] = prunedItinStagedAt;
  }

  return { drafts: prunedDrafts, stagedAt: prunedStagedAt };
}
